import { ButtonBase, styled, Tooltip } from '@mui/material';
import { useCallback, useState } from 'react';

const Root = styled(ButtonBase)(() => ({
  position: 'relative',
  padding: '0px',
  margin: '0px',
  outline: 'none',
  overflow: 'visible',
  minHeight: '36px',
  minWidth: '36px',
}));

const Outer = styled('div')(({ theme, borderColor }) => ({
  padding: '2px',
  border: `1px solid ${borderColor}`,
  borderRadius: '6px',
  backgroundColor: '#CCD4D6',
  height: '100%',
  width: '100%',
  transition: 'border-color .1s, background .1s',
  // states
  '[data-hover=true] &': {
    backgroundColor: theme.palette.porcelain[300],
  },
  '[data-active=true] &': {
    backgroundColor: theme.palette.shamrock['400Base'],
  },
  '[data-variant=active] &': {
    backgroundColor: theme.palette.shamrock['400Base'],
    borderColor: theme.palette.shamrock[600],
  },
  '.Mui-disabled &': {
    background: '#DDE2E4',
    border: '1px solid #BAC5C9',
  },
}));

const InnerBorder = styled('div')(({ theme }) => ({
  background: 'linear-gradient(#F7F7F7, #C4D0D4)',
  borderRadius: '4px',
  width: '100%',
  height: '100%',
  boxShadow:
    '0 11px 3px 0 rgba(32, 44, 49, 0.00), 0 7px 3px 0 rgba(32, 44, 49, 0.04), 0 4px 2px 0 rgba(32, 44, 49, 0.26), 0 2px 2px 0 rgba(32, 44, 49, 0.22), 0 0 1px 0 rgba(32, 44, 49, 0.26);',

  padding: '1px',
  //states
  '[data-active=true] &': {
    boxShadow:
      '0 11px 3px 0 rgba(32, 44, 49, 0.00), 0 7px 3px 0 rgba(32, 44, 49, 0.04), 0 2px 2px 0 rgba(32, 44, 49, 0.22), 0 0 1px 0 rgba(32, 44, 49, 0.26)',
  },
  '[data-variant=active] &': {
    boxShadow: `0 2px 15px 0 ${theme.palette.shamrock['400Base']}`,
  },
  '.Mui-disabled &': {
    boxShadow:
      '0 11px 3px 0 rgba(32, 44, 49, 0.00), 0 7px 3px 0 rgba(32, 44, 49, 0.04), 0 2px 2px 0 rgba(32, 44, 49, 0.22), 0 0 1px 0 rgba(32, 44, 49, 0.26)',
    background: 'linear-gradient(#EBEBEB, #E2E7E9)',
  },
}));

const Inner = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  borderRadius: '3px',
  background: 'linear-gradient(139deg, #DEE3E3 2.2%, #F4F5F5 90.82%)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  //states
  '[data-variant=active] &': {
    color: theme.palette.shamrock[600],
  },
  '.Mui-disabled &': {
    color: theme.palette.iron[300],
    background: 'linear-gradient( #D3D9D9, #E4E7E7)',
  },
}));

export const ButtonPush = ({
  children,
  disabled,
  height = '36px',
  width = '36px',
  borderColor = '#a9b6bc',
  variant = 'default',
  tooltipText = '',
  onClick,
  ...otherProps
}) => {
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  const handleEnter = useCallback(() => {
    if (!disabled) setHover(true);
  }, [disabled]);
  const handleLeave = useCallback(() => {
    if (!disabled) setHover(false), setActive(false);
  }, []);
  const handleMouseDown = useCallback(() => {
    if (!disabled) setActive(true);
  }, [disabled]);
  const handleMouseUp = useCallback(() => {
    setActive(false);
  }, []);
  // specific on click handler for actual button action
  const handleClick = useCallback(
    (event) => {
      if (!disabled && onClick) {
        onClick(event);
      }
    },
    [disabled, onClick],
  );

  return (
    <Root
      data-variant={variant}
      disabled={disabled}
      data-hover={hover ? 'true' : 'false'}
      data-active={active ? 'true' : 'false'}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
      aria-label={tooltipText || undefined}
      disableRipple
      sx={{
        width: width,
        height: height,
      }}
      {...otherProps}
    >
      <Outer borderColor={borderColor}>
        <InnerBorder>
          <Tooltip title={tooltipText} enterDelay={1000}>
            <Inner>{children}</Inner>
          </Tooltip>
        </InnerBorder>
      </Outer>
    </Root>
  );
};
